import '@/App.css';

import { CssBaseline, ThemeProvider } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';

import { useGetUserPreferencesQuery } from '@/api/v1';
import { AppErrorBoundary } from '@/components/atoms/AppErrorBoundary';
import { ChunkLoadErrorBoundary } from '@/components/atoms/ChunkLoadErrorBoundary';
import { configuration } from '@/config';
import { ColorSchemeValueContext } from '@/context/colorSchemeContext';
import { TimezoneContext } from '@/hooks/useDateFormatter';
import { defaultLocale, LocaleContext } from '@/hooks/useTranslator';
import { AuthenticationGuard } from '@/Routing/AuthenticationGuard';
import { authSelector } from '@/StateManagement/AuthReducer';
import createDashboardTheme from '@/styles/theme';
import { Locale } from '@/types/locale';

const LocaleAndTimezoneProviders: React.FC<React.PropsWithChildren> = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const { user } = useSelector(authSelector);
  const { data: preferences } = useGetUserPreferencesQuery(undefined, { skip: !user });

  const language = React.useMemo(
    (): Locale => preferences?.language ?? defaultLocale,
    [preferences],
  );

  const timezone = React.useMemo(
    (): string => preferences?.timezone ?? configuration.defaultTimezone,
    [preferences],
  );

  return (
    <LocaleContext.Provider value={language}>
      <TimezoneContext.Provider value={timezone}>{children}</TimezoneContext.Provider>
    </LocaleContext.Provider>
  );
};

const CalizaCustomerDashboard: React.FC = (): React.ReactElement => {
  const colorScheme = React.useContext<'dark' | 'light'>(ColorSchemeValueContext);

  return (
    <ThemeProvider theme={createDashboardTheme(colorScheme)}>
      <CssBaseline />
      <LocaleAndTimezoneProviders>
        <AppErrorBoundary>
          <Router>
            <ChunkLoadErrorBoundary>
              <AuthenticationGuard />
            </ChunkLoadErrorBoundary>
          </Router>
        </AppErrorBoundary>
      </LocaleAndTimezoneProviders>
    </ThemeProvider>
  );
};

export default CalizaCustomerDashboard;
